import { apiRequestNoThrow } from "@/lib/queryClient";
import type { VerifySearchResponse, VerifySearchErrorResponse } from "@server/routes/blockchain/dto";

/**
 * Verifies a search value (address, transaction, block height, or block hash)
 * by calling the backend which validates against Blockstream API
 */
export const verifySearch = async (
  searchValue: string
): Promise<VerifySearchResponse> => {
  const response = await apiRequestNoThrow("POST", "/api/blockchain/verify-search", {
    searchValue,
  });

  if (!response.ok) {
    try {
      const errorData = await response.json() as VerifySearchErrorResponse;
      throw new Error(errorData.error || "The wallet address, block height, block hash, or transaction you entered is invalid or not found");
    } catch (parseError) {
      // If JSON parsing fails, throw a generic error
      throw new Error("The wallet address, block height, block hash, or transaction you entered is invalid or not found");
    }
  }

  return response.json();
};

/**
 * Verifies if a Bitcoin address is valid by checking with Blockstream API
 */
export const verifyAddress = async (address: string): Promise<{ valid: boolean; address: string }> => {
  const trimmedAddress = address.trim();
  const response = await apiRequestNoThrow("GET", `/api/blockchain/address/${encodeURIComponent(trimmedAddress)}/verify`);

  if (!response.ok) {
    try {
      const errorData = await response.json() as any;
      throw new Error(errorData.message || errorData.error || "The provided address is not a valid Bitcoin address. Please check the address and try again.");
    } catch (parseError) {
      // If the error is already an Error from above, re-throw it
      if (parseError instanceof Error && parseError.message !== "Failed to parse") {
        throw parseError;
      }
      // If JSON parsing fails, throw a generic error
      throw new Error("The provided address is not a valid Bitcoin address. Please check the address and try again.");
    }
  }

  try {
    return await response.json();
  } catch (parseError) {
    throw new Error("Failed to verify address. Please try again.");
  }
};
